import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { Column } from "react-table";
import { toast } from "react-toastify";

import { Result } from "./SerialNumberKeysTable";
import Checked from "components/icons/Checked";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import IndeterminateBox from "components/icons/IndeterminateBox";
import { SuccessIcon } from "components/icons/SuccessIcon";
import Unchecked from "components/icons/Unchecked";
import KebabMenu from "components/kebab-menu/KebabMenu";
import layoutStyle from "components/layout/layout.scss";
import { LicenseKeyMenuItems } from "components/licenses/assign-bms-keys/LicenseKeyMenuItems";
import filteringStyle from "components/licenses/filter-license-keys/license-key-filtering.scss";
import { LicenseKeysFiltering } from "components/licenses/filter-license-keys/LicenseKeysFiltering";
import { LicenseKeyActions } from "components/licenses/license-key-deletion/LicenseKeyActions";
import style from "components/licenses/license-keys-table.scss";
import { ShortUrlTableCell } from "components/licenses/ShortUrlTableCell";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import tableStyle from "components/reports/erasure-reports-table.scss";
import SearchView from "components/search/SearchView";
import DateCell from "components/table/DateCell";
import Table from "components/table/Table";
import TextWithTooltip from "components/table/TextWithTooltip";
import { AUTH_LICENSE_KEY_MANAGEMENT, AUTH_LICENSE_POOL_VIEW } from "domain/authority";
import { SUPPORT_EMAIL } from "domain/globalConstants";
import { LicenseKeyFilter } from "domain/licenseKeys";
import { Key, KeyCursor, LicenseData } from "domain/licenses";
import { licenseService } from "services/licenses/LicenseService";
import { Action, Category, Label, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { isCurrentUserInternal } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import buttonStyle from "styles/buttons.scss";
import filterStyle from "styles/layout.scss";
import { resolveColor } from "utils/commonFunctions";
import { RepositoryKey } from "utils/repository";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface TableState {
    licenseKeys?: Key[];
    cursor?: KeyCursor | null;
    scrollPosition?: number;
}

interface Props {
    count: number;
    setExportButtonVisibility: (visible: boolean) => void;
    availableLicenses: LicenseData[];
    loading: boolean;
    filterType: LicenseKeyFilter;
    setFilterType: (filterType: LicenseKeyFilter) => void;
    setQuickSearch: (quickSearch: string) => void;
    quickSearch?: string;
}

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
    hasBmsLicenses: state.licensesReducer.hasBmsLicenses,
    tenantDetails: state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1],
    loggedInUser: state.userReducer.user,
});
const connector = connect(mapState, {});

const LicenseKeysTable = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [deselected, setDeselected] = React.useState(false);
    const [okClicked, setOkClicked] = React.useState<boolean>(false);
    const [reloadLicenseKeys, setReloadLicenseKeys] = React.useState<boolean>(false);
    const [result, setResult] = React.useState<Result>({ title: "", message: "" });
    const showResult = (resultToShow: Result) => {
        setResult(resultToShow);
    };
    const LICENSE_KEY_SELECTION_LIMIT = 1000;

    const licenseTypes: Map<string, string> = new Map([
        ["bms-validation", "Validation"],
        ["bms-bbti", "Buy-Back / Trade In"],
        ["bms-lease", "Lease"],
        ["bms-insurance", "Insurance"],
        ["bms-ntf", "My Device Health"],
        ["bms-all", "All Features"],
        ["usdk", "Mobile Software Development Kit (SDK)"],
    ]);
    const isDeleteLicenseKeysActive =
        userSessionService.userHasAllAuthorities([AUTH_LICENSE_KEY_MANAGEMENT]) && isCurrentUserInternal();
    const [licenseKeysData, setLicenseKeysData] = React.useState<TableState>({
        licenseKeys: [],
    });
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const allLicenseKeysReference = React.useRef<Key[]>(licenseKeysData.licenseKeys ?? []);
    const [deletingKeys, setDeletingKeys] = React.useState<boolean>(false);

    const isLicenseKeySelected = (licenseKey: string): boolean => {
        return (
            typeof allLicenseKeysReference.current.find((each) => each.licenseKey === licenseKey && each.checked) !==
            "undefined"
        );
    };

    const isAnyLicenseKeySelected = (): boolean => {
        return typeof allLicenseKeysReference.current.find((each) => each.checked) !== "undefined";
    };

    const hasAllLicenseKeysSelected = (): boolean => {
        const selectableKeys = Math.min(allLicenseKeysReference.current.length, LICENSE_KEY_SELECTION_LIMIT);
        return (
            selectableKeys > 0 &&
            allLicenseKeysReference.current.slice(0, LICENSE_KEY_SELECTION_LIMIT).every((each) => each.checked)
        );
    };

    const getSelectedCount = (): number => {
        return allLicenseKeysReference.current.filter((key) => key.checked).length;
    };

    const createBmsKeySelectionCheckbox = (licenseKey: string) => {
        const isSelected = isLicenseKeySelected(licenseKey);
        const selectedCount = getSelectedCount();
        const isDisabled = !isSelected && selectedCount >= LICENSE_KEY_SELECTION_LIMIT;
        return (
            <label className={tableStyle.container}>
                <input
                    data-testid={testIds.common.primaryView.table.selectRowCheckbox}
                    key={licenseKey}
                    name="licenseKeys"
                    id={licenseKey}
                    type="checkbox"
                    checked={isSelected && !deselected}
                    disabled={isDisabled}
                    value={licenseKey}
                    onChange={(e) => handleCheckboxChange(e, licenseKey)}
                />
                <span className={tableStyle.checkmark} />
            </label>
        );
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, licenseKey: string) => {
        setDeselected(false);
        if (event.target.checked) {
            const selectedCount = getSelectedCount();
            if (selectedCount >= LICENSE_KEY_SELECTION_LIMIT) {
                return;
            }
            updateTableStateBasedOnLicenseKey(true, licenseKey);
        } else {
            updateTableStateBasedOnLicenseKey(false, licenseKey);
        }
    };

    const updateBmsKeyState = (checkedValue: boolean) => {
        allLicenseKeysReference.current = allLicenseKeysReference.current.map((each, index) => {
            each.checked = checkedValue ? index < LICENSE_KEY_SELECTION_LIMIT : false;
            return each;
        });

        setLicenseKeysData((prevState) => ({
            ...prevState,
            licenseKeys: prevState.licenseKeys?.map((each, index) => {
                each.checked = checkedValue ? index < LICENSE_KEY_SELECTION_LIMIT : false;
                return each;
            }),
        }));
    };

    const updateTableStateBasedOnLicenseKey = (checkedValue: boolean, selectedLicenseKey: string) => {
        allLicenseKeysReference.current = allLicenseKeysReference.current.map((each) => {
            if (each.licenseKey === selectedLicenseKey) {
                each.checked = checkedValue;
            }
            return each;
        });

        setLicenseKeysData((prevState) => ({
            ...prevState,
            licenseKeys: prevState.licenseKeys?.map((each) => {
                if (each.licenseKey === selectedLicenseKey) {
                    each.checked = checkedValue;
                }
                return each;
            }),
        }));
    };

    React.useEffect(() => {
        if (okClicked) {
            setLicenseKeysData({
                licenseKeys: [],
                cursor: null,
                scrollPosition: 0,
            });
            fetchData(true);

            setOkClicked(false);
        }
    }, [okClicked, reloadLicenseKeys]);

    const createColumns = () => {
        const columns: Array<Column<Key>> = [];
        isDeleteLicenseKeysActive &&
            columns.push({
                Header: () =>
                    isDeleteLicenseKeysActive && (
                        <div className={tableStyle.selectAllCheckbox}>
                            <label className={tableStyle.selectAllCheckboxContainer}>
                                <input
                                    data-testid={testIds.common.primaryView.table.selectAllCheckbox}
                                    type="checkbox"
                                    checked={hasAllLicenseKeysSelected()}
                                    onChange={(event) => {
                                        if (event.target.checked) {
                                            updateBmsKeyState(true);
                                        } else {
                                            updateBmsKeyState(false);
                                        }
                                    }}
                                />
                                <div className={tableStyle.customCheckboxIcon}>
                                    {isAnyLicenseKeySelected() ? (
                                        !hasAllLicenseKeysSelected() ? (
                                            <IndeterminateBox
                                                backgroundColor={resolveColor(
                                                    "--app-buttons-buttonPrimary-backgroundColor"
                                                )}
                                                lineColor={resolveColor("--color-white")}
                                            />
                                        ) : (
                                            <Checked
                                                backgroundColor={resolveColor(
                                                    "--app-buttons-buttonPrimary-backgroundColor"
                                                )}
                                                checkColor={resolveColor("--color-white")}
                                            />
                                        )
                                    ) : (
                                        <Unchecked
                                            borderColor={resolveColor("--app-tables-tableRowDividerColor")}
                                            backgroundColor={resolveColor("--app-checkbox-customCheckboxIcon")}
                                        />
                                    )}
                                </div>
                            </label>
                        </div>
                    ),
                accessor: "checked",
                minWidth: 70,
                maxWidth: 70,
                Cell: ({
                    cell: {
                        row: { original: licenseKeys },
                    },
                }) => {
                    return (
                        <>
                            <div className={classNames(tableStyle.flexDisplay)}>
                                <div className={tableStyle.flexDisplay}>
                                    {createBmsKeySelectionCheckbox(licenseKeys.licenseKey)}
                                </div>
                            </div>
                        </>
                    );
                },
            });
        columns.push(
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.licenseKey")} key="licenseKey" />,
                accessor: "licenseKey",
                Cell: ({ cell: { row, value } }) => {
                    return (
                        <div className={style.shortUrlCell}>
                            {userSessionService.userHasAllAuthorities([AUTH_LICENSE_KEY_MANAGEMENT]) &&
                                props.loggedInUser &&
                                props.loggedInUser.tenantType === "INTERNAL" &&
                                props.availableLicenses && (
                                    <div className={style.kebabIcon}>
                                        <KebabMenu>
                                            <LicenseKeyMenuItems
                                                licenseKey={row.original}
                                                fromBms={true}
                                                availableLicenses={props.availableLicenses}
                                                setReloadLicenseKeys={setReloadLicenseKeys}
                                            />
                                        </KebabMenu>
                                    </div>
                                )}
                            <TextWithTooltip text={value} disabled={!row.original.enabled} />
                        </div>
                    );
                },
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.license")} key="licenseType" />,
                accessor: "licenseType",
                Cell: ({ cell: { row, value } }) => (
                    <TextWithTooltip
                        text={licenseTypes.has(value) ? (licenseTypes.get(value) as string) : ""}
                        disabled={!row.original.enabled}
                    />
                ),
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.identifier")} key="identifier" />,
                accessor: "identifier",
                Cell: ({ cell: { row, value } }) => <TextWithTooltip text={value} disabled={!row.original.enabled} />,
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.assigned")} key="assigned" />,
                accessor: "assigned",
                Cell: ({ row, cell: { value } }) => (
                    <TextWithTooltip text={value.toString()} disabled={!row.original.enabled} />
                ),
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.used")} key="used" />,
                accessor: "used",
                Cell: ({ row, cell: { value } }) => (
                    <TextWithTooltip text={value.toString()} disabled={!row.original.enabled} />
                ),
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.remaining")} key="remaining" />,
                accessor: "remaining",
                Cell: ({ row, cell: { value } }) => (
                    <TextWithTooltip text={value.toString()} disabled={!row.original.enabled} />
                ),
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.startDate")} key="startDate" />,
                accessor: "startDate",
                Cell: ({ row, cell: { value } }) => (
                    <DateCell withoutTime={true} tooltip={true} value={value} disabled={!row.original.enabled} />
                ),
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.expirationDate")} key="expirationDate" />,
                accessor: "expirationDate",
                Cell: ({ row, cell: { value } }) => (
                    <DateCell withoutTime={true} tooltip={true} value={value} disabled={!row.original.enabled} />
                ),
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.usageHoursLimit")} key="usageHoursLimit" />,
                accessor: "usageHoursLimit",
                Cell: ({ row, cell: { value } }) => (
                    <TextWithTooltip text={value.toString()} disabled={!row.original.enabled} />
                ),
            },
            {
                Header: () => <TextWithTooltip text={t("LicenseKeys.shortUrl")} key="shortUrl" />,
                accessor: "shortUrl",
                width: 230,
                Cell: (cellInfo) => <ShortUrlTableCell shortUrl={cellInfo.cell.row.original.shortUrl} />,
            }
        );

        userSessionService.userHasAllAuthorities([AUTH_LICENSE_POOL_VIEW]) &&
            columns.push({
                Header: () => <TextWithTooltip text={t("LicensePools.view.poolName")} />,
                accessor: "poolName",
                Cell: ({ row }) => <TextWithTooltip text={row.original.poolName} disabled={!row.original.enabled} />,
            });
        return columns;
    };
    const columns = createColumns();

    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const [initialLoading, setInitialLoading] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);

    const fetchData = (initialLoading: boolean) => {
        setLoading(true);
        setInitialLoading(initialLoading);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        licenseService
            .fetchLicenseKeys(
                abortController,
                "bms",
                initialLoading ? "" : licenseKeysData.cursor?.licenseKey,
                initialLoading ? "" : licenseKeysData.cursor?.tenantIdentifier,
                initialLoading ? "" : licenseKeysData.cursor?.created,
                props.quickSearch,
                props.filterType
            )
            .then((licenseKey) => {
                // First, License Keys are sorted in descending order by Start Date
                // Second, License Keys are sorted in ascending order by identifier: if identifier is null, empty string is considered for sorting
                licenseKey.licenseKeys.licenseKeys?.sort(
                    (a, b) =>
                        b.startDate.localeCompare(a.startDate) || (a.identifier ?? "").localeCompare(b.identifier ?? "")
                );
                let filteredKeys = licenseKey.licenseKeys.licenseKeys?.map((key) => ({ ...key, checked: false })) ?? [];
                if (props.filterType === LicenseKeyFilter.DISABLED) {
                    filteredKeys = filteredKeys.filter((key) => key.remaining === 0 || key.enabled === false);
                }

                setLicenseKeysData((prevState) => {
                    const updatedKeys = initialLoading
                        ? filteredKeys
                        : (prevState.licenseKeys?.concat(filteredKeys) ?? filteredKeys);
                    allLicenseKeysReference.current = updatedKeys; // Use the updated keys, not stale state
                    return {
                        ...prevState,
                        licenseKeys: updatedKeys,
                        scrollPosition: initialLoading
                            ? 0
                            : prevState.licenseKeys
                              ? prevState.licenseKeys.length - 1
                              : -1,
                        cursor: licenseKey.licenseKeys.cursor,
                    };
                });
                props.setExportButtonVisibility((filteredKeys?.length ?? 0) > 0);
                setLoading(false);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("LicenseKeys.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                    setInitialLoading(false);
                    if (deletingKeys) {
                        setDeletingKeys(false);
                    }
                }
            });
    };

    React.useEffect(() => {
        allLicenseKeysReference.current = licenseKeysData.licenseKeys ?? [];
    }, [licenseKeysData.licenseKeys]);

    React.useEffect(() => {
        setLicenseKeysData({
            licenseKeys: [],
            cursor: null,
            scrollPosition: 0,
        });
        fetchData(true);
        if (reloadLicenseKeys) {
            setReloadLicenseKeys(false);
        }
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, [props.count, props.quickSearch, props.filterType, reloadLicenseKeys]);

    function findSelectedLicenseKeys(): string[] {
        return licenseKeysData.licenseKeys?.filter((each) => each.checked).map((each) => each.licenseKey) ?? [];
    }

    function findSelectedLicenseKeysWithStatus(): { licenseKey: string; enabled: boolean }[] {
        return (
            licenseKeysData.licenseKeys
                ?.filter((each) => each.checked)
                .map((each) => ({ licenseKey: each.licenseKey, enabled: each.enabled ?? true })) ?? []
        );
    }

    const handleEnableDisableKeys = async (action: "ENABLE" | "DISABLE") => {
        setLoading(true);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        try {
            const selectedKeysWithStatus = findSelectedLicenseKeysWithStatus();
            const keysToUpdate = selectedKeysWithStatus
                .filter((key) => (action === "ENABLE" ? !key.enabled : key.enabled))
                .map((key) => key.licenseKey);

            if (keysToUpdate.length === 0) {
                setLoading(false);
                return;
            }

            await licenseService.updateLicenseKeysStatus(keysToUpdate, action === "ENABLE", abortController);
            const message =
                action === "ENABLE"
                    ? t("LicenseKeys.enabledSuccessMessage", { count: keysToUpdate.length })
                    : t("LicenseKeys.disabledSuccessMessage", { count: keysToUpdate.length });
            toast(
                <SuccessIcon
                    successClass={layoutStyle.customToastSuccessIcon}
                    color={theme.contentBackgroundColor}
                    text={message}
                />,
                {
                    closeButton: (closeToastProps) => (
                        <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                    ),
                    className: layoutStyle.customToastSuccessMessage,
                }
            );
            setLicenseKeysData({
                licenseKeys: [],
                cursor: null,
                scrollPosition: 0,
            });
        } catch {
            if (!abortController.signal.aborted) {
                const mailToLink = `mailto:${SUPPORT_EMAIL}`;
                showResult({
                    title: t("LicenseKeys.updateFailedTitle"),
                    message: (
                        <span>
                            {t("LicenseKeys.updateFailedMessage")}
                            <a href={mailToLink}>{t("DeleteLicenseKey.supportMessage")} </a>
                        </span>
                    ),
                });
            }
        } finally {
            setLoading(false);
            fetchData(true);
        }
    };
    const selectionInformation = (
        <LicenseKeyActions
            selectedCount={findSelectedLicenseKeys().length}
            onDeleteError={() => {
                setDeletingKeys(false);
                updateBmsKeyState(false);
            }}
            onDeselect={() => {
                updateBmsKeyState(false);
            }}
            onEnableDisable={handleEnableDisableKeys}
            onDeleteSuccess={() => {
                setReloadLicenseKeys(true);
                setDeletingKeys(false);
            }}
            result={result}
            onDeleteStart={() => setDeletingKeys(true)}
            getSelectedKeys={findSelectedLicenseKeys}
            getSelectedKeysWithStatus={findSelectedLicenseKeysWithStatus}
            testIdPrefix="bms"
            abortControllers={React.useRef(abortControllers)}
        />
    );

    return props.loading || deletingKeys ? (
        <LoadingIndicator />
    ) : (
        <>
            <div className={filteringStyle.tableHeader}>
                <LicenseKeysFiltering
                    setFilterType={props.setFilterType}
                    filterType={props.filterType}
                    resultsCount={licenseKeysData.licenseKeys?.length ?? 0}
                />
                {isAnyLicenseKeySelected() && (
                    <div className={filteringStyle.searchContainer}>
                        {selectionInformation}
                        <SearchView setSearch={props.setQuickSearch} searchInProgress={false} />
                    </div>
                )}
                {!isAnyLicenseKeySelected() && <SearchView setSearch={props.setQuickSearch} searchInProgress={false} />}
            </div>
            <div className={filterStyle.tableWrapper}>
                <Table
                    tableIdentity={RepositoryKey.LICENSE_TABLE}
                    data={licenseKeysData.licenseKeys ?? []}
                    columns={columns}
                    loaded={!initialLoading}
                    failureMessage={requestFailureMessage}
                    scrollTo={licenseKeysData.scrollPosition}
                    tooltips={true}
                    emptyMessage={t("LicenseKeys.emptyStateMessage", { email: SUPPORT_EMAIL })}
                    testId={testIds.workArea.license.bms.table.itself}
                />
            </div>
            {licenseKeysData.cursor != null &&
                (licenseKeysData.licenseKeys?.length ?? 0) != 0 &&
                requestFailureMessage === "" &&
                (loading ? (
                    <LoadingIndicator small={true} withTable />
                ) : (
                    <button
                        className={buttonStyle.loadMoreButtonWithoutIcon}
                        onClick={() => {
                            fetchData(false);
                            usageStatisticsService.sendEvent({
                                label: Label.BMS,
                                action: Action.LOAD_MORE,
                                category: Category.LICENSE,
                            });
                        }}
                        data-testid={testIds.common.primaryView.table.loadMoreButton}
                    >
                        {t("Common.loadMore")}
                    </button>
                ))}
        </>
    );
};

export default connector(LicenseKeysTable);
